import type { BinaryOperator, Filter } from "@cubejs-client/core"
import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { useSearchParams } from "react-router-dom"
import type { SupportedPlatform } from "~/ui-rtk/api/types/paid-media.types"
import { isVideoUrl } from "~/ui-rtk/components/atoms/MarathonImage"
import { BrandMediaSocialPostsDashboard } from "~/ui-rtk/components/features/dashboards"
import WithLoader from "~/ui-rtk/components/layout/WithLoader/WithLoader"
import Checkbox from "~/ui-rtk/components/ui/controls/Checkbox"
import type {
  TableVisualizationColumn,
  TMetric,
} from "~/ui-rtk/constants/table-visualization-types"
import useConnect, {
  BRAND_MEDIA_SOCIAL_POSTS_TABLES,
  type TBrandMediaSocialPostsTableId,
} from "~/ui-rtk/pages/BrandMedia/OrganicSocialPosts/connect"
import BlastTargetPickerModal, {
  type BlastSource,
} from "./BlastTargetPickerModal"

/**
 * AdBlaster — Social Posts tab.
 *
 * Embeds BrandMedia/OrganicSocialPosts's dashboard so editors get the polished
 * chrome (sort, filters, column picker, date range, last-updated chip,
 * pagination) for free. Three knobs adapt it to the launcher use case:
 *
 *   - `hideBreadcrumb`     — AdBlaster owns the page header.
 *   - `items` (trimmed)    — only `ALL_POSTS / FACEBOOK / INSTAGRAM` are
 *                            included so the channel-tabs strip renders
 *                            three filter buttons (TikTok/YouTube hidden
 *                            because they aren't blastable today).
 *   - `extraCubeFilters`   — AND-merged into the ALL_POSTS widget's cube
 *                            filter to scope rows to IG + FB. The
 *                            per-channel widgets (FACEBOOK / INSTAGRAM)
 *                            apply their own `media_channel` filter and
 *                            don't need this.
 *   - `leadingColumn` +    — inject a row-select checkbox + selection state
 *     `renderConfigOverride` so users can multi-blast.
 *
 * Selection is keyed by `post_id`. The bulk-action bar above the dashboard
 * appears when ≥1 row is checked and opens the shared BlastTargetPickerModal
 * with `kind: "post"` sources, which fans out to `useBlastPostMutation`.
 */

const PREFIX = "company_media_metrics_organic_posts"
const POST_ID_KEY = `${PREFIX}.post_id`
const POST_PREVIEW_KEY = `${PREFIX}.post_preview`
const POST_CAPTION_KEY = `${PREFIX}.post_caption`
const POST_CHANNEL_KEY = `${PREFIX}.media_channel`
/** Cube `post_link` holds the video URL for video posts (a plain link for others). */
const POST_VIDEO_KEY = `${PREFIX}.post_link`

function previewTypeOf(
  preview: string,
  videoUrl?: string,
): "image" | "video" | null {
  if (videoUrl) return "video"
  return preview ? "image" : null
}

/** Only treat a cached URL as playable media when it points at a video file. */
function postVideoUrl(value: unknown): string | undefined {
  const url = String(value ?? "")
  return /^https?:\/\//i.test(url) && isVideoUrl(url) ? url : undefined
}

/** Cube stores the channel as "facebook" / "instagram" (any casing); anything else is unknown. */
function postChannel(value: unknown): "facebook" | "instagram" | undefined {
  const channel = String(value ?? "").toLowerCase()
  return channel === "facebook" || channel === "instagram" ? channel : undefined
}

function truncate(text: string, max: number): string {
  if (text.length <= max) return text
  return `${text.slice(0, max - 1)}…`
}

// Lifted out of the component so the dashboard's `columnConfig` memo doesn't
// see a fresh object on every render — that would force a table re-mount and
// reset sort/filter state.
function makeSelectColumn(): TableVisualizationColumn {
  return {
    key: "ad-blaster-select",
    header: "",
    type: "text",
    width: 40,
    disableSorting: true,
    disableFiltering: true,
    classes: {
      head: "text-center",
      cell: "text-center",
    },
    render: (row: TMetric, cfg) => {
      const postId = String(row[POST_ID_KEY] ?? "")
      if (!postId) return ""
      const selectedIds = (cfg?.selectedIds as Set<string> | undefined) ?? null
      const onToggle = cfg?.onToggle as ((id: string) => void) | undefined
      const isChecked = selectedIds?.has(postId) ?? false
      // Wrap in a span that stops click propagation so the row-level
      // onRowClick handler (which also toggles selection) doesn't fire
      // alongside the checkbox's onChange. Without this the two toggles
      // cancel out and clicking the checkbox looks like a no-op while
      // clicking elsewhere on the row works (because only the row handler
      // fires).
      return (
        <span
          onClick={e => e.stopPropagation()}
          className="inline-flex h-full w-full items-center justify-center"
        >
          <Checkbox checked={isChecked} onChange={() => onToggle?.(postId)} />
        </span>
      )
    },
  }
}

const SELECT_COLUMN = makeSelectColumn()

export default function SocialPostsTab() {
  const { content, isLoading } = useConnect()
  const [searchParams, setSearchParams] = useSearchParams()

  // Drop the channel-specific tab definitions so the dashboard's default-tab
  // resolver lands on ALL_POSTS. The channel strip is already hidden, but
  // narrowing the items map avoids URL-param drift surfacing a hidden tab.
  // Include All / Facebook / Instagram in the items map so the dashboard's
  // channel-tabs strip renders three filter buttons. The dashboard handles
  // the per-channel cube filter via each item's `mediaChannel` — so clicking
  // "Instagram" automatically narrows the query to media_channel='instagram'.
  // TikTok / YouTube are excluded because we only blast IG + FB today.
  const items = useMemo(() => {
    const wanted: TBrandMediaSocialPostsTableId[] = [
      BRAND_MEDIA_SOCIAL_POSTS_TABLES.ALL_POSTS as TBrandMediaSocialPostsTableId,
      BRAND_MEDIA_SOCIAL_POSTS_TABLES.FACEBOOK as TBrandMediaSocialPostsTableId,
      BRAND_MEDIA_SOCIAL_POSTS_TABLES.INSTAGRAM as TBrandMediaSocialPostsTableId,
    ]
    const next = {} as typeof content.content.items
    for (const key of wanted) {
      const entry = content.content.items[key]
      if (entry) next[key] = entry
    }
    return Object.keys(next).length > 0 ? next : content.content.items
  }, [content])

  const extraCubeFilters = useMemo<Filter[]>(
    () => [
      {
        dimension: `${PREFIX}.media_channel`,
        operator: "equals" as BinaryOperator,
        values: ["instagram", "facebook"],
      },
    ],
    [],
  )

  // Selection state — keyed by Cube `post_id` so it survives sort/filter/page
  // changes inside the underlying TanStack table (which would otherwise
  // shuffle row indices).
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set())
  // Mirrors the row data the dashboard last rendered, so the bulk-blast
  // builder can resolve a post_id back to its caption / preview without
  // re-firing the Cube query here.
  const [rowsById, setRowsById] = useState<Map<string, TMetric>>(new Map())

  const toggleSelected = useCallback((id: string) => {
    setSelectedIds(prev => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }, [])

  // Deeplink consumption: when OrganicSocialPosts navigates here with
  // `?postId=<id>`, pre-check that row's checkbox so the bulk-blast bar
  // appears immediately. Strip the param afterwards so a manual reload of
  // the URL doesn't re-trigger the pre-selection (and so the user can clear
  // the selection without it springing back). Guarded with a ref so the
  // effect only acts once per navigation, even if `searchParams` is mutated
  // by an unrelated consumer of `useSearchParams`.
  const deeplinkAppliedRef = useRef(false)
  useEffect(() => {
    if (deeplinkAppliedRef.current) return
    const postId = searchParams.get("postId")
    if (!postId) return
    setSelectedIds(prev => {
      if (prev.has(postId)) return prev
      const next = new Set(prev)
      next.add(postId)
      return next
    })
    deeplinkAppliedRef.current = true
    // Clear the search param without adding a history entry.
    const nextParams = new URLSearchParams(searchParams)
    nextParams.delete("postId")
    setSearchParams(nextParams, { replace: true })
  }, [searchParams, setSearchParams])

  // The checkbox render function captures this row on first touch so we can
  // resolve selections back to caption/preview without a second cube fetch.
  const captureRow = useCallback((row: TMetric) => {
    const id = String(row[POST_ID_KEY] ?? "")
    if (!id) return
    setRowsById(prev => {
      if (prev.has(id)) return prev
      const next = new Map(prev)
      next.set(id, row)
      return next
    })
  }, [])

  const renderConfigOverride = useMemo(
    () => ({
      selectedIds,
      onToggle: (id: string) => {
        toggleSelected(id)
      },
      captureRow,
    }),
    [selectedIds, toggleSelected, captureRow],
  )

  // Row click toggles selection rather than opening the PostDetails drawer.
  // Captures the row (same as the checkbox cell does) so the bulk-blast
  // builder can resolve the post later.
  const handleRowClick = useCallback(
    (row: Record<string, unknown>) => {
      const id = String(row[POST_ID_KEY] ?? "")
      if (!id) return
      captureRow(row as TMetric)
      toggleSelected(id)
    },
    [captureRow, toggleSelected],
  )

  const [blastModalOpen, setBlastModalOpen] = useState(false)

  const blastSources = useMemo<BlastSource[]>(() => {
    const list: BlastSource[] = []
    for (const id of selectedIds) {
      const row = rowsById.get(id)
      const caption = row ? String(row[POST_CAPTION_KEY] ?? "") : ""
      const preview = row ? String(row[POST_PREVIEW_KEY] ?? "") : ""
      const channel = row ? postChannel(row[POST_CHANNEL_KEY]) : undefined
      // Some FB video posts cache the video itself as their "preview".
      const videoUrl = row
        ? (postVideoUrl(row[POST_VIDEO_KEY]) ?? postVideoUrl(preview))
        : undefined
      list.push({
        kind: "post",
        id,
        name: caption ? truncate(caption, 64) : id,
        platform: "meta" as SupportedPlatform,
        postId: id,
        ...(channel ? { channel } : {}),
        ...(videoUrl ? { videoUrl } : {}),
        previewUrl: preview || null,
        previewType: previewTypeOf(preview, videoUrl),
        primaryText: caption || null,
      })
    }
    return list
  }, [selectedIds, rowsById])

  // Wrap the select column with the row-capture side-effect so resolving
  // selections later doesn't need a second cube fetch.
  const leadingColumns = useMemo<TableVisualizationColumn[]>(
    () => [
      {
        ...SELECT_COLUMN,
        render: (row, cfg) => {
          const capture = cfg?.captureRow as ((r: TMetric) => void) | undefined
          capture?.(row)
          return SELECT_COLUMN.render?.(row, cfg)
        },
      },
    ],
    [],
  )

  return (
    <WithLoader isLoading={isLoading}>
      <div className="flex flex-col gap-system-sm">
        {selectedIds.size > 0 && (
          <div className="flex items-center justify-between rounded-md border border-system-blue-600 bg-system-blue-700 px-system-md py-system-sm">
            <span className="text-sm text-white">
              {selectedIds.size} post{selectedIds.size === 1 ? "" : "s"}{" "}
              selected
            </span>
            <div className="flex items-center gap-system-sm">
              <button
                type="button"
                onClick={() => setSelectedIds(new Set())}
                className="text-sm text-system-gray-200 hover:text-white"
              >
                Clear
              </button>
              <button
                type="button"
                onClick={() => setBlastModalOpen(true)}
                className="rounded-md bg-system-green-600 px-system-md py-system-2xs text-sm font-medium text-white hover:bg-system-green-500"
              >
                Blast {selectedIds.size}
              </button>
            </div>
          </div>
        )}

        <BrandMediaSocialPostsDashboard
          slug={content.slug}
          items={items}
          title={content.title}
          parent={content.parent}
          lastDateChipProps={content.content.lastDateChipProps}
          hideBreadcrumb
          extraCubeFilters={extraCubeFilters}
          leadingColumns={leadingColumns}
          renderConfigOverride={renderConfigOverride}
          onRowClick={handleRowClick}
        />

        <BlastTargetPickerModal
          open={blastModalOpen}
          onClose={() => setBlastModalOpen(false)}
          sources={blastSources}
        />
      </div>
    </WithLoader>
  )
}
